import React from "react";
import ConnectButton from "../components/ConnectButton";
import Footer from "../sections/Footer";
import UpgradeTokens from "../components/UpgradeTokens";
import { Web3ModalProvider } from "../components/Web3ModalProvider";
import TokensInfo from "../components/TokensInfo";
import TransactionList from "../components/TransactionList";
import NetworkConfigNotice from "../components/NetworkConfigNotice";
import SwapButtons from "../components/SwapButtons";
import { motion } from "framer-motion";

const ConvertPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      <Web3ModalProvider>
        <div className="relative min-h-screen py-12 sm:py-16 flex flex-col justify-start overflow-hidden">
          {/* Animated Background */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.1 }}
            transition={{ duration: 2 }}
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 50% 50%, #FF6B6B 1px, transparent 1px)",
              backgroundSize: "30px 30px",
            }}
          />

          {/* Floating Elements */}
          <motion.div
            animate={{
              y: [0, -20, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute left-10 top-32 w-32 h-32 bg-gradient-to-r from-orange-400/30 to-yellow-400/30 rounded-full blur-xl"
          />
          <motion.div
            animate={{
              y: [0, 20, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute right-10 bottom-32 w-32 h-32 bg-gradient-to-r from-teal-400/30 to-blue-400/30 rounded-full blur-xl"
          />

          <div className="relative z-10 max-w-6xl mx-auto px-4">
            {/* Page Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-20"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl mb-8 shadow-xl"
              >
                <span className="text-3xl">🏦</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-4xl lg:text-5xl font-bold mb-6 text-slate-900 tracking-tight"
              >
                Token Conversion Center
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-lg text-slate-600 max-w-2xl mx-auto mb-12 leading-relaxed"
              >
                Convert your BSC tokens (NBM/ASSET) to Hedera network tokens.
                Simple, secure, and efficient token migration.
              </motion.p>

              {/* How It Works Overview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white rounded-2xl p-8 max-w-5xl mx-auto shadow-lg border border-slate-200"
              >
                <div className="text-center mb-10">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-100 rounded-xl mb-4">
                    <span className="text-2xl">🔄</span>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    How It Works
                  </h3>
                  <p className="text-slate-600">
                    Four simple steps to convert your tokens
                  </p>
                </div>

                <div className="grid md:grid-cols-4 gap-6">
                  {/* Step 1 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    whileHover={{ y: -4 }}
                    className="text-center group"
                  >
                    <div className="relative mb-4">
                      <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center text-white font-bold text-lg mx-auto shadow-lg group-hover:shadow-blue-500/25 transition-all duration-300">
                        1
                      </div>
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">
                      Connect Wallet
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Link your BSC wallet securely
                    </p>
                  </motion.div>

                  {/* Step 2 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    whileHover={{ y: -4 }}
                    className="text-center group"
                  >
                    <div className="relative mb-4">
                      <div className="w-14 h-14 bg-green-500 rounded-xl flex items-center justify-center text-white font-bold text-lg mx-auto shadow-lg group-hover:shadow-green-500/25 transition-all duration-300">
                        2
                      </div>
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">
                      Select Tokens
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Choose NBM or ASSET tokens
                    </p>
                  </motion.div>

                  {/* Step 3 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                    whileHover={{ y: -4 }}
                    className="text-center group"
                  >
                    <div className="relative mb-4">
                      <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center text-white font-bold text-lg mx-auto shadow-lg group-hover:shadow-orange-500/25 transition-all duration-300">
                        3
                      </div>
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">
                      Convert
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Burn BSC, receive Hedera tokens
                    </p>
                  </motion.div>

                  {/* Step 4 */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                    whileHover={{ y: -4 }}
                    className="text-center group"
                  >
                    <div className="relative mb-4">
                      <div className="w-14 h-14 bg-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-lg mx-auto shadow-lg group-hover:shadow-purple-500/25 transition-all duration-300">
                        4
                      </div>
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">
                      Complete
                    </h4>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Receive tokens on Hedera
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Network Configuration Notice - Full Width */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-16 px-2 sm:px-4"
          >
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 sm:p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-3 sm:mr-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <span className="text-lg sm:text-xl">⚠️</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-amber-900 mb-2">
                    Network Setup Required
                  </h3>
                  <p className="text-amber-800 mb-3 sm:mb-4 text-xs sm:text-sm leading-relaxed">
                    Configure your wallet correctly before starting the
                    conversion process.
                  </p>
                  <div className="bg-white rounded-lg">
                    <NetworkConfigNotice />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="relative z-10 max-w-6xl mx-auto px-4">
            {/* Step 1: Token Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mb-16"
            >
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center text-white font-semibold mr-4">
                  1
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    Check Your Token Information
                  </h2>
                  <p className="text-slate-600 text-sm">
                    View current balances and market data
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-lg">📋</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1 text-sm">
                      What this shows:
                    </h4>
                    <p className="text-blue-800 text-xs leading-relaxed">
                      Current token prices and conversion rates to help you make
                      informed decisions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mr-4">
                    <span className="text-2xl">📊</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      Token Information
                    </h3>
                    <p className="text-slate-600 text-sm">Real-time data</p>
                  </div>
                </div>
                <TokensInfo />
              </div>
            </motion.div>

            {/* Step 2: Connect Wallet & Convert */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mb-16"
            >
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center text-white font-semibold mr-4">
                  2
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    Connect Wallet & Start Conversion
                  </h2>
                  <p className="text-slate-600 text-sm">
                    Securely connect and convert your tokens
                  </p>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-lg">🔗</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1 text-sm">
                      How it works:
                    </h4>
                    <p className="text-green-800 text-xs leading-relaxed">
                      Connect your BSC wallet to convert NBM and ASSET tokens.
                      The conversion burns BSC tokens and send you ASSET tokens
                      on Hedera network.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mr-4">
                      <span className="text-2xl">🔄</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">
                        Token Conversion
                      </h3>
                      <p className="text-slate-600 text-sm">
                        Secure blockchain migration
                      </p>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-lg">💡</span>
                      </div>
                      <div className="text-left">
                        <h4 className="font-semibold text-amber-900 mb-1 text-sm">
                          Important Notice:
                        </h4>
                        <p className="text-amber-800 text-xs leading-relaxed">
                          Ensure you have enough BNB for gas fees.
                        </p>
                      </div>
                    </div>
                  </div>

                  <ConnectButton />
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <UpgradeTokens />
                </div>
              </div>
            </motion.div>

            {/* Step 3: Track Your Transactions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mb-16"
            >
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center text-white font-semibold mr-4">
                  3
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    Track Your Transaction History
                  </h2>
                  <p className="text-slate-600 text-sm">
                    Monitor conversion progress and status
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mr-4">
                    <span className="text-2xl">📋</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      Transaction History
                    </h3>
                    <p className="text-slate-600 text-sm">
                      Real-time transaction monitoring
                    </p>
                  </div>
                </div>

                <TransactionList />
              </div>
            </motion.div>

            {/* Step 4: Purchase Additional Tokens */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="mb-16"
            >
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white font-semibold mr-4">
                  4
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    Purchase Additional Tokens
                  </h2>
                  <p className="text-slate-600 text-sm">
                    Optional: Buy more tokens
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mr-4">
                    <span className="text-2xl">🛒</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      Token Purchase
                    </h3>
                    <p className="text-slate-600 text-sm">
                      Buy from decentralized exchanges
                    </p>
                  </div>
                </div>
                <SwapButtons title="Buy Tokens" />
              </div>
            </motion.div>

            {/* Help Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="mt-12"
            >
              <div className="bg-slate-50 rounded-xl p-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-slate-200 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🤝</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">
                    Need Help?
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                    <div className="bg-white rounded-lg p-4">
                      <span className="text-xl block mb-2">❓</span>
                      <h4 className="font-semibold text-slate-900 mb-2 text-sm">
                        Common Issues
                      </h4>
                      <p className="text-slate-600 text-xs">
                        Ensure you have BNB for gas fees and wallet is connected
                        to BSC network
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <span className="text-xl block mb-2">⏱️</span>
                      <h4 className="font-semibold text-slate-900 mb-2 text-sm">
                        Processing Time
                      </h4>
                      <p className="text-slate-600 text-xs">
                        Conversions take 5-15 minutes depending on network
                        congestion
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <span className="text-xl block mb-2">🔒</span>
                      <h4 className="font-semibold text-slate-900 mb-2 text-sm">
                        Security
                      </h4>
                      <p className="text-slate-600 text-xs">
                        Tokens processed through secure smart contracts
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </Web3ModalProvider>
      <Footer />
    </div>
  );
};

export default ConvertPage;
